import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, CheckCircle, AlertTriangle, Users, UserPlus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import StatCard from '../components/StatCard'
import { StatusBadge } from '../components/OrgCard'

interface OrgRow {
  id: string
  name: string
  email: string
  status: string
  plan_name: string
  expires_at: string
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({ orgs: 0, active: 0, expiringSoon: 0, users: 0, newLeads: 0 })
  const [recent, setRecent] = useState<OrgRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const now = new Date()
      const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
      const [orgsRes, activeRes, expRes, usersRes, leadsRes, recentRes] = await Promise.all([
        supabase.from('organizations').select('id', { count: 'exact', head: true }),
        supabase.from('org_subscriptions').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('org_subscriptions').select('id', { count: 'exact', head: true })
          .eq('status', 'active').lte('expires_at', in30),
        supabase.from('users').select('id', { count: 'exact', head: true }),
        supabase.from('jumpstart_leads').select('id', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('organizations')
          .select(`id, name, email, status, org_subscriptions(status, expires_at, subscription_plans(name))`)
          .order('created_at', { ascending: false })
          .limit(10),
      ])
      setStats({
        orgs: orgsRes.count ?? 0,
        active: activeRes.count ?? 0,
        expiringSoon: expRes.count ?? 0,
        users: usersRes.count ?? 0,
        newLeads: leadsRes.count ?? 0,
      })
      const rows = (recentRes.data ?? []).map((o: any) => {
        const sub = Array.isArray(o.org_subscriptions) ? o.org_subscriptions[0] : o.org_subscriptions
        return {
          id: o.id, name: o.name, email: o.email ?? '—',
          status: sub?.status ?? o.status ?? '—',
          plan_name: sub?.subscription_plans?.name ?? '—',
          expires_at: sub?.expires_at ?? '',
        }
      })
      setRecent(rows)
      setLoading(false)
    }
    load()
  }, [])

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
    </div>
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>Overview of the Otomator platform</p>
      </div>

      {/* KPI Cards — 2 cols on mobile, 4 on xl */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">
        <StatCard title="New Leads" value={stats.newLeads} icon={UserPlus} color="#ec4899" />
        <StatCard title="Organizations" value={stats.orgs} icon={Building2} color="#6366f1" />
        <StatCard title="Active Subs" value={stats.active} icon={CheckCircle} color="#22c55e" />
        <StatCard title="Expiring (30d)" value={stats.expiringSoon} icon={AlertTriangle} color="#f59e0b" />
        <StatCard title="Total Users" value={stats.users} icon={Users} color="#3b82f6" />
      </div>

      {/* Recent Orgs */}
      <div className="rounded-xl overflow-hidden" style={{ background: '#252836', border: '1px solid #2d3148' }}>
        <div className="px-4 sm:px-6 py-4 border-b" style={{ borderColor: '#2d3148' }}>
          <h2 className="text-white font-semibold">Recent Organizations</h2>
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid #2d3148' }}>
                {['Name', 'Email', 'Status', 'Plan', 'Expires', ''].map(h => (
                  <th key={h} className="text-left px-6 py-3 font-medium" style={{ color: '#9ca3af' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((org, i) => (
                <tr
                  key={org.id}
                  className="cursor-pointer transition-colors hover:bg-white/5"
                  style={{ borderBottom: i < recent.length - 1 ? '1px solid #2d3148' : undefined }}
                  onClick={() => navigate(`/organizations/${org.id}`)}
                >
                  <td className="px-6 py-3 text-white font-medium">{org.name}</td>
                  <td className="px-6 py-3 text-sm" style={{ color: '#9ca3af' }}>{org.email}</td>
                  <td className="px-6 py-3"><StatusBadge status={org.status} /></td>
                  <td className="px-6 py-3">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>{org.plan_name}</span>
                  </td>
                  <td className="px-6 py-3 text-sm" style={{ color: '#9ca3af' }}>
                    {org.expires_at ? new Date(org.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      className="text-xs px-3 py-1 rounded-lg"
                      style={{ background: '#6366f120', color: '#a5b4fc' }}
                      onClick={e => { e.stopPropagation(); navigate(`/organizations/${org.id}`) }}
                    >View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden divide-y" style={{ borderColor: '#2d3148' }}>
          {recent.map(org => (
            <div
              key={org.id}
              className="px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => navigate(`/organizations/${org.id}`)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-medium text-sm truncate">{org.name}</div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: '#9ca3af' }}>{org.email}</div>
                </div>
                <StatusBadge status={org.status} />
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>{org.plan_name}</span>
                {org.expires_at && (
                  <span className="text-xs" style={{ color: '#6b7280' }}>
                    Expires {new Date(org.expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
